import { forwardRef } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
//
import { LabelProps } from './types';

// ----------------------------------------------------------------------

const Label = forwardRef<HTMLSpanElement, LabelProps>(
  ({ children, color = 'default', variant = 'soft', startIcon, endIcon, sx, ...other }, ref) => {
    const theme = useTheme();

    const iconStyle = {
      width: 16,
      height: 16,
      '& svg, img': { width: 1, height: 1, objectFit: 'cover' },
    };

    const getColors = () => {
      switch (color) {
        case 'primary':
          return {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          };
        case 'secondary':
          return {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
          };
        case 'info':
          return {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.info.contrastText,
          };
        case 'success':
          return {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
          };
        case 'warning':
          return {
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          };
        case 'error':
          return {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
          };
        default:
          return {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.common.white,
          };
      }
    };

    const getVariantStyles = () => {
      switch (variant) {
        case 'filled':
          return {
            ...getColors(),
            border: 'none',
          };
        case 'outlined':
          return {
            ...getColors(),
            backgroundColor: 'transparent',
            border: `2px solid ${getColors().backgroundColor}`,
          };
        case 'soft':
          return {
            backgroundColor: theme.palette.grey[500],
            color: theme.palette.text.secondary,
            border: 'none',
          };
        default:
          return {};
      }
    };

    return (
      <Box
        ref={ref}
        component="span"
        ownerState={{ color, variant }}
        sx={{
          // @ts-ignore
          ...getVariantStyles(),
          ...(startIcon && { pl: 0.75 }),
          ...(endIcon && { pr: 0.75 }),
          ...sx,
          px: 1,
          py: 0.5,
          fontSize: '0.8rem',
          borderRadius: 1,
        }}
        theme={theme}
        {...other}
      >
        {startIcon && <Box sx={{ mr: 0.75, ...iconStyle }}> {startIcon} </Box>}

        {children}

        {endIcon && <Box sx={{ ml: 0.75, ...iconStyle }}> {endIcon} </Box>}
      </Box>
    );
  }
);

export default Label;
